import { addDays, endOfWeek } from "date-fns";
import _ from "lodash";
import moment from "moment";
import type { TFunction } from "src/design-system/core/TranslationContext";
import { getLocale } from "src/i18n";
import { formatStringTime } from "./formatters";

export const THIRTY = 30;

export const getRoundedDate = (minutes: number, d = new Date()): Date => {
    const ms = 1000 * 60 * minutes; // convert minutes to ms
    const roundedDate = new Date(Math.round(d.getTime() / ms) * ms);

    return roundedDate;
};

export const isFutureDate = (date: Date): boolean => {
    return date > new Date();
};

export const isFutureTwoNextWeekDate = (date: Date): boolean => {
    return moment(date).isAfter(endOfWeek(addDays(new Date(), 7), { weekStartsOn: 1 }));
};

export const optionsDateFull: Intl.DateTimeFormatOptions = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
};

export const getFullStringOfDate = (date: Date): string => {
    return date.toLocaleDateString(getLocale(), optionsDateFull).split(" ").slice(0, 4).join(" ");
};

export function getTimeInMsFromString(strTime: string, seconds: number): number {
    const [hours, minutes, secondsTemp] = strTime.split(":");
    if (secondsTemp) {
        seconds = parseInt(secondsTemp, 10);
    }

    return (
        parseInt(hours, 10) * 60 * 60 * 1000 + parseInt(minutes, 10) * 60 * 1000 + seconds * 1000
    );
}

export const buildHoursSlotString = (
    translateCommon: TFunction,
    minTime = "00:00",
    maxTime = "23:59",
    minutes = [0, 15, 30, 45]
): string[] => {
    const hours: string[] = [];

    const minHour = parseInt(minTime.split(":")[0], 10);
    const maxHour = parseInt(maxTime.split(":")[0], 10);

    _.range(minHour, maxHour + 1).forEach((hour) => {
        minutes.forEach((minute) => {
            const time = formatStringTime(`${hour}:${minute}`, translateCommon);
            if (minTime <= time && time <= maxTime) {
                hours.push(time);
            }
        });
    });

    return hours;
};

export const getOnlyHourMinuteFromString = (value: string): string => {
    return value.length === 8 ? value.slice(0, 5) : value.slice(0, 4);
};

export function getNextThirtyMinutes(date: moment.Moment, securityInterval = 0): moment.Moment {
    // securityInterval en minute
    if (date.minutes() % THIRTY < securityInterval) {
        return moment(date).subtract(date.minutes() % THIRTY, "minutes");
    }
    const remainder = THIRTY - (date.minutes() % THIRTY);
    return moment(date).add(remainder, "minutes");
}

export function getNextHalfHourTime(time: number): number {
    // l'arrondi permet d'éviter les valeurs très proches de 0.3 (comme 0.2999999997) qui faussent le reste des calculs
    // le math.round(X / 0.01)*0.01 permet d'arrondir au 0.01 le plus proche
    const startTimeMinutes = Math.round((time - Math.trunc(time)) / 0.01) * 0.01;
    const remainder = 0.3 - (startTimeMinutes % 0.3);
    const startTimeNextHalfHourAddMinutes = startTimeMinutes + remainder;
    const startTimeNextHalfHour =
        Math.trunc(time) +
        (startTimeNextHalfHourAddMinutes > 0.59 ? 1 : 0) +
        (startTimeNextHalfHourAddMinutes > 0.59
            ? startTimeNextHalfHourAddMinutes - 0.6
            : startTimeNextHalfHourAddMinutes);
    return startTimeNextHalfHour;
}

export function isSameDuration(
    value1: moment.Duration,
    value2: moment.Duration,
    tolerance: moment.Duration | undefined = undefined
): boolean {
    if (!tolerance) {
        return value1.asMilliseconds() === value2.asMilliseconds();
    } else {
        const difference = value1.clone().subtract(value2);
        return Math.abs(difference.asMilliseconds()) < tolerance.asMilliseconds();
    }
}
